import React from 'react';
import { ArrowRightIcon, PencilIcon } from '@heroicons/react/24/outline';
import { SalesOrder } from '../../types/sales.types';
import { Modal } from '../ui/Modal';
import QuoteDocumentPanel from './QuoteDocumentPanel';
import { usePermissions } from '../../hooks/usePermissions';
import {
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  formatMoney,
  formatNumber,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';

interface ViewQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  quote?: SalesOrder;
  onEdit?: (quote: SalesOrder) => void;
  onConvert?: (quote: SalesOrder) => void;
}

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const ViewQuoteModal: React.FC<ViewQuoteModalProps> = ({
  isOpen,
  onClose,
  quote,
  onEdit,
  onConvert,
}) => {
  const { can } = usePermissions();
  if (!quote) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Quote Details" size="xl">
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p>
              <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                {quote.orderNumber}
                {quote.quoteRevision ? ` Rev ${quote.quoteRevision}` : ''}
              </span>
            </p>
            <h4 className="mt-2 text-lg font-semibold text-gray-900">
              {quote.customer?.name || 'Unknown Customer'}
            </h4>
            {quote.customer?.code && <p className="text-sm text-gray-500">{quote.customer.code}</p>}
          </div>
          <span className={`${badgeClass} text-blue-700 bg-blue-50 ring-blue-600/20`}>Quote</span>
        </div>

        <QuoteDocumentPanel quoteId={quote.id} customerEmail={quote.customer?.email} canSend={can('sales.update')} />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <DetailCard title="Quote">
            <DetailItem label="Quote date">{formatDate(quote.orderDate)}</DetailItem>
            <DetailItem label="Expected delivery">{formatDate(quote.expectedDeliveryDate)}</DetailItem>
            <DetailItem label="Payment terms">{quote.paymentTerms || '—'}</DetailItem>
            <DetailItem label="Delivery terms">{quote.deliveryTerms || '—'}</DetailItem>
          </DetailCard>

          <DetailCard title="Delivery">
            <DetailItem label="Shipping method">{quote.shippingMethod || '—'}</DetailItem>
            <DetailItem label="Delivery location">{quote.deliveryLocation || '—'}</DetailItem>
            <DetailItem label="Promised date">{formatDate(quote.promisedDate)}</DetailItem>
            <DetailItem label="Confirmed date">{formatDate(quote.confirmedDate)}</DetailItem>
          </DetailCard>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Product</th>
                <th className={`${thClass} text-right`}>Qty</th>
                <th className={`${thClass} text-right`}>Unit Price</th>
                <th className={`${thClass} text-right`}>Discount</th>
                <th className={`${thClass} text-right`}>Tax</th>
                <th className={`${thClass} text-right`}>Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {(quote.items ?? []).length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={6}>
                    This quote has no items
                  </td>
                </tr>
              ) : (
                quote.items.map((item, index) => (
                  <tr key={item.id || index}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.productName}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatNumber(item.quantity)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatMoney(item.unitPrice)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatMoney(item.discountAmount)}
                      {Number(item.discountRate) > 0 && (
                        <span className="text-gray-500"> ({formatNumber(Number(item.discountRate), 2)}%)</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatMoney(item.taxAmount)}
                      <span className="text-gray-500"> ({Number(item.taxRate || 0)}%)</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">
                      {formatMoney(item.totalPrice)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Shipping address</p>
              <p className="mt-1 whitespace-pre-wrap text-gray-900">{quote.shippingAddress || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</p>
              <p className="mt-1 whitespace-pre-wrap text-gray-900">{quote.notes || '—'}</p>
            </div>
            {quote.specialInstructions && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Special instructions
                </p>
                <p className="mt-1 whitespace-pre-wrap text-gray-900">{quote.specialInstructions}</p>
              </div>
            )}
          </div>
          <dl className="self-start rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
            <div className="flex justify-between py-1">
              <dt className="text-gray-600">Subtotal</dt>
              <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(quote.totalAmount)}</dd>
            </div>
            <div className="flex justify-between py-1">
              <dt className="text-gray-600">Discount</dt>
              <dd className="font-medium text-gray-900 tabular-nums">-{formatMoney(quote.discountAmount)}</dd>
            </div>
            <div className="flex justify-between py-1">
              <dt className="text-gray-600">Tax</dt>
              <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(quote.taxAmount)}</dd>
            </div>
            <div className="mt-2 flex justify-between border-t border-gray-200 pt-3">
              <dt className="font-semibold text-gray-900">Grand Total</dt>
              <dd className="text-base font-semibold text-gray-900 tabular-nums">
                {formatMoney(quote.grandTotal)}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <FormActions>
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
        {onEdit && (
          <button type="button" onClick={() => onEdit(quote)} className={secondaryButtonClass}>
            <PencilIcon className="mr-2 h-4 w-4" />
            Edit Quote
          </button>
        )}
        {onConvert && (
          <button type="button" onClick={() => onConvert(quote)} className={primaryButtonClass}>
            <ArrowRightIcon className="mr-2 h-4 w-4" />
            Convert to Order
          </button>
        )}
      </FormActions>
    </Modal>
  );
};

export default ViewQuoteModal;
